import { useEffect, useMemo, useRef, useState } from "react";

import {
  formatTimeAsCountdown,
  getTimeUntilFutureDate,
} from "../utils/helpers";

interface UseCountdownProps {
  enabled?: boolean;
  futureDateOrTotalTimeInMs?: string | number;
  showDays?: boolean;
  showHours?: boolean;
}

const COUNTDOWN_DELAY = 1000; // 1 second;

function getTimeLeft(
  futureDate: string | number,
  showDays?: boolean,
  showHours?: boolean
) {
  if (typeof futureDate === "string") {
    const timeInMs = getTimeUntilFutureDate(futureDate);
    return formatTimeAsCountdown(timeInMs, showDays, showHours);
  }
  return formatTimeAsCountdown(futureDate, showDays, showHours);
}

function useCountdown({
  enabled = true,
  futureDateOrTotalTimeInMs,
  showDays,
  showHours,
}: UseCountdownProps) {
  const [timeLeft, setTimeLeft] = useState<string | undefined>();
  const timeoutIdRef = useRef<NodeJS.Timeout>();

  const futureDate = useMemo(() => {
    if (!enabled) return;

    if (typeof futureDateOrTotalTimeInMs === "number") {
      return new Date(
        Date.now() + futureDateOrTotalTimeInMs + COUNTDOWN_DELAY
      ).toISOString();
    }

    return futureDateOrTotalTimeInMs;
  }, [enabled, futureDateOrTotalTimeInMs]);

  useEffect(() => {
    if (!futureDate) return;

    clearTimeout(timeoutIdRef.current);

    const timeoutId = setTimeout(() => {
      const time = getTimeLeft(futureDate, showDays, showHours);
      setTimeLeft(time);
    }, 1000);

    timeoutIdRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
    };
  }, [futureDate, showDays, showHours, timeLeft]);

  return timeLeft;
}

export default useCountdown;
